/**
 * Disabling eslint rule as currently it does not support currying of custom hooks
 * and thinks we are calling it
 */
/* eslint-disable react-hooks/rules-of-hooks */
/**
 * Disabling this as putting in exhaustive deps recommended causes infinite re-rendering from reference
 * changing
 */
/* eslint-disable react-hooks/exhaustive-deps */
import { ServiceResponse } from "@rsm-hcd/javascript-core";
import {
    ServiceHookFactory as RsmServiceHookFactory,
    useAbortSignal,
    useCancellablePromise,
} from "@rsm-hcd/javascript-react";
import { useCallback } from "react";
import ServiceFactory, {
    BatchedListService,
    BulkCreateService,
    BulkDeleteService,
    BulkUpdateService,
    CreateFromPostParamsService,
    CreateService,
    DeleteService,
    ListService,
    NestedBatchedListService,
    NestedCreateFromPostParamsService,
    NestedCreateService,
    NestedListService,
    RecordType,
    StreamServiceWithSignal,
    UpdateFromPutParamsService,
    UpdateService,
} from "utilities/services/service-factory";

// -----------------------------------------------------------------------------------------
// #region Types
// -----------------------------------------------------------------------------------------

/**
 * Type defining the return object from calling `useBulkCreate()`
 */
export type BulkCreateServiceHook<TRecord> = () => {
    create: BulkCreateService<TRecord>;
};

/**
 * Type defining the return object from calling `useBulkUpdate()`
 */
export type BulkUpdateServiceHook<TRecord, TPathParams> = () => {
    update: BulkUpdateService<TRecord, TPathParams>;
};

/**
 * Type defining the return object from calling `useCreate()`
 */
export type CreateServiceHook<TRecord> = () => {
    create: CreateService<TRecord>;
};

export type CreateFromPostParamsServiceHook<TRecord, TPostParams> = () => {
    create: CreateFromPostParamsService<TRecord, TPostParams>;
};

/**
 * Type defining the return object from calling `useDelete()`
 */
export type DeleteServiceHook = () => {
    delete: DeleteService;
};

/**
 * Type defining the return object from calling `useBulkDelete()`
 */
export type BulkDeleteServiceHook<TPathParams, TQueryParams> = () => {
    bulkDelete: BulkDeleteService<TPathParams, TQueryParams>;
};

/**
 * Type defining the return object from calling `useList()`
 */
export type ListServiceHook<TRecord, TQueryParams> = () => {
    list: ListService<TRecord, TQueryParams>;
};

/**
 * Type defining the return object from calling `useListBatched()`
 */
export type BatchedListServiceHook<TRecord, TQueryParams> = () => {
    list: BatchedListService<TRecord, TQueryParams>;
    // TODO: Will need to have batching as this is abstracted
    // onBatchError: (queryParamValues: Array<any>) => void;
    // onBatchSuccess: (items: Array<TRecord>) => void;
};

export type NestedBatchedListServiceHook<TRecord, TPathParams, TQueryParams> =
    () => {
        list: NestedBatchedListService<TRecord, TPathParams, TQueryParams>;
        // TODO: Will need to have batching as this is abstracted
        // onBatchError: (queryParamValues: Array<any>) => void;
        // onBatchSuccess: (items: Array<TRecord>) => void;
    };

/**
 * Type defining the return object from calling `useNestedCreate()`
 */
export type NestedCreateServiceHook<TRecord, TPathParams> = () => {
    create: NestedCreateService<TRecord, TPathParams>;
};

export type NestedCreateFromPostParamsServiceHook<
    TRecord,
    TPostParams,
    TPathParams,
> = () => {
    create: NestedCreateFromPostParamsService<
        TRecord,
        TPostParams,
        TPathParams
    >;
};

/**
 * Type defining the return object from calling `useNestedList()`
 */
export type NestedListServiceHook<TRecord, TPathParams, TQueryParams> = () => {
    list: NestedListService<TRecord, TPathParams, TQueryParams>;
};

/**
 * Type defining the return object from calling `useUpdate()`
 */
export type UpdateServiceHook<
    TRecord,
    TPathParams,
    TQueryParams = undefined,
> = () => {
    update: UpdateService<TRecord, TPathParams, TQueryParams>;
};

/**
 * Type defining the return object from calling `useUpdateFromPutParams()`
 */
export type UpdateFromPutParamsServiceHook<
    TRecord,
    TPutParams,
    TPathParams,
    TQueryParams = undefined,
> = () => {
    update: UpdateFromPutParamsService<
        TRecord,
        TPutParams,
        TPathParams,
        TQueryParams
    >;
};

/**
 * Type defining the service function for patching the supplied resource type.
 *
 * @param {TPathParams} pathParams Values to populate dynamic slugs in the resource route.
 * @param {TPatchParams} values Values to pass into the body of the HTTP Request.
 */
export type PatchService<
    TRecord,
    TPathParams = undefined,
    TPatchParams = undefined,
> = (
    pathParams?: TPathParams,
    values?: TPatchParams
) => Promise<ServiceResponse<TRecord>>;

/**
 * Custom React Hook to invoke HTTP PATCH requests.
 */
export type PatchServiceHook<
    TRecord,
    TPathParams = undefined,
    TPatchParams = undefined,
> = () => {
    patch: PatchService<TRecord, TPathParams, TPatchParams>;
};

/**
 * Custom React Hook to invoke HTTP streaming requests.
 * This is used for long-running requests that return a stream of data.
 */
export type StreamServiceHook<
    TRecord extends RecordType,
    TPathParams = undefined,
> = () => {
    stream: StreamServiceWithSignal<TRecord, TPathParams>;
};

// #endregion Types

// ---------------------------------------------------------------------------------------------
// #region Functions
// ---------------------------------------------------------------------------------------------

/**
 * Factory to encapsulate common service function logic
 */
const ServiceHookFactory = {
    usePatch: <TRecord, TPathParams = undefined, TPatchParams = undefined>(
        recordType: { new (): TRecord },
        resourceEndpoint: string
    ): PatchServiceHook<TRecord, TPathParams, TPatchParams> => {
        const usePatch = () => {
            const { cancellablePromise } = useCancellablePromise();

            const servicePatch = ServiceFactory.patch<
                TRecord,
                TPathParams,
                TPatchParams
            >(recordType, resourceEndpoint);

            const patch: PatchService<TRecord, TPathParams, TPatchParams> =
                useCallback(
                    (
                        pathParams?: TPathParams,
                        values?: TPatchParams
                    ): Promise<ServiceResponse<TRecord>> =>
                        cancellablePromise(servicePatch(pathParams, values)),
                    [cancellablePromise, servicePatch]
                );

            return { patch: patch };
        };

        return usePatch;
    },
    useBulkCreate<TRecord extends RecordType>(
        recordType: { new (): TRecord },
        baseEndpoint: string
    ): BulkCreateServiceHook<TRecord> {
        return () => {
            const { cancellablePromise } = useCancellablePromise();

            const serviceBulkCreate = ServiceFactory.bulkCreate(
                recordType,
                baseEndpoint
            );

            function create(
                records?: Array<TRecord>
            ): Promise<ServiceResponse<TRecord>> {
                return cancellablePromise(
                    serviceBulkCreate(records)
                ) as Promise<ServiceResponse<TRecord>>;
            }

            return { create: useCallback(create, []) };
        };
    },

    /**
     * Creates conventional hook for using batched service list function for the supplied resource type.
     * Automatically handles cancellation tokens internally.
     *
     * ### Recommendation
     * Use `useNestedBatchedList` when route is nested!
     *
     * @param recordType
     * @param baseEndpoint
     * @param property
     * @param size
     */
    useBatchedList<TRecord, TQueryParams>(
        recordType: { new (): TRecord },
        baseEndpoint: string
    ): BatchedListServiceHook<TRecord, TQueryParams> {
        return () => {
            const { cancellablePromise } = useCancellablePromise();

            const serviceList = ServiceFactory.batchedList<
                TRecord,
                TQueryParams
            >(recordType, baseEndpoint);

            function list(
                queryParams: TQueryParams,
                property: keyof TQueryParams,
                size: number
            ): Promise<ServiceResponse<TRecord>> {
                return cancellablePromise(
                    serviceList(queryParams, property, size)
                ) as Promise<ServiceResponse<TRecord>>;
            }

            return { list: useCallback(list, []) };
        };
    },

    /**
     * Creates conventional hook for using service create function for the supplied resource type.
     * Automatically handles cancellation tokens internally.
     *
     * ### Recommendation
     * Use `useNestedCreate` when route is nested!
     *
     * @param recordType
     * @param baseEndpoint
     */
    useCreate<TRecord extends RecordType>(
        recordType: { new (): TRecord },
        baseEndpoint: string
    ): CreateServiceHook<TRecord> {
        return () => {
            const { cancellablePromise } = useCancellablePromise();

            const serviceCreate = ServiceFactory.create(
                recordType,
                baseEndpoint
            );

            function create(
                record?: TRecord
            ): Promise<ServiceResponse<TRecord>> {
                return cancellablePromise(
                    serviceCreate(record ?? new recordType())
                ) as Promise<ServiceResponse<TRecord>>;
            }

            return { create: useCallback(create, []) };
        };
    },

    /**
     * Creates conventional hook for using service create function for the supplied resource type
     * when creating with a set of parameters rather than a full record.  Automatically handles
     * cancellation tokens internally.
     *
     * @param recordType
     * @param baseEndpoint
     */
    useCreateFromPostParams<TRecord extends RecordType, TPostParams>(
        recordType: { new (): TRecord },
        baseEndpoint: string
    ): CreateFromPostParamsServiceHook<TRecord, TPostParams> {
        return () => {
            const { cancellablePromise } = useCancellablePromise();

            const serviceCreate = ServiceFactory.createFromPostParams<
                TRecord,
                TPostParams
            >(recordType, baseEndpoint);

            function create(
                requestData?: TPostParams,
                signal?: AbortSignal
            ): Promise<ServiceResponse<TRecord>> {
                return cancellablePromise(
                    serviceCreate(requestData ?? ({} as TPostParams), signal)
                ) as Promise<ServiceResponse<TRecord>>;
            }

            return { create: useCallback(create, []) };
        };
    },

    /**
     * Creates conventional hook for using service create function for the supplied nested
     * resource type when creating with a set of parameters rather than a full record.
     * Automatically handles cancellation tokens internally.
     *
     * @param recordType
     * @param baseEndpoint
     */
    useNestedCreateFromPostParams<
        TRecord extends RecordType,
        TPostParams,
        TPathParams,
    >(
        recordType: { new (): TRecord },
        baseEndpoint: string
    ): NestedCreateFromPostParamsServiceHook<
        TRecord,
        TPostParams,
        TPathParams
    > {
        return () => {
            const { cancellablePromise } = useCancellablePromise();

            const serviceCreate = ServiceFactory.nestedCreateFromPostParams<
                TRecord,
                TPostParams,
                TPathParams
            >(recordType, baseEndpoint);

            function create(
                pathParams: TPathParams,
                requestData?: TPostParams,
                signal?: AbortSignal
            ): Promise<ServiceResponse<TRecord>> {
                return cancellablePromise(
                    serviceCreate(
                        pathParams,
                        requestData ?? ({} as TPostParams),
                        signal
                    )
                ) as Promise<ServiceResponse<TRecord>>;
            }

            return { create: useCallback(create, []) };
        };
    },

    /**
     * Creates conventional hook for using service delete function for the supplied resource type.
     * Automatically handles cancellation tokens internally.
     * @param recordType
     * @param baseEndpoint
     */
    useDelete<TPathParams extends any>(
        resourceEndpoint: string
    ): DeleteServiceHook {
        return () => {
            const { cancellablePromise } = useCancellablePromise();

            const serviceDelete =
                ServiceFactory.delete<TPathParams>(resourceEndpoint);

            function _delete(
                id: number | string,
                pathParams?: any
            ): Promise<ServiceResponse<Boolean>> {
                return cancellablePromise(
                    serviceDelete(id, pathParams)
                ) as Promise<ServiceResponse<Boolean>>;
            }

            return { delete: useCallback(_delete, []) };
        };
    },

    /**
     * Creates conventional hook for using service bulk delete function for the supplied resource type.
     * Automatically handles cancellation tokens internally.
     * @param resourceEndpoint
     */
    useBulkDelete<TPathParams extends any, TQueryParams extends any>(
        resourceEndpoint: string
    ): BulkDeleteServiceHook<TPathParams, TQueryParams> {
        return () => {
            const { cancellablePromise } = useCancellablePromise();

            const serviceBulkDelete = ServiceFactory.bulkDelete<
                TPathParams,
                TQueryParams
            >(resourceEndpoint);

            function bulkDelete(
                pathParams?: TPathParams,
                queryParams?: TQueryParams
            ): Promise<ServiceResponse<Boolean>> {
                return cancellablePromise(
                    serviceBulkDelete(pathParams, queryParams)
                ) as Promise<ServiceResponse<Boolean>>;
            }

            return { bulkDelete: useCallback(bulkDelete, []) };
        };
    },

    /**
     * Creates conventional hook for using service get function for the supplied resource type.
     * Automatically handles cancellation tokens internally.
     * @param recordType
     * @param resourceEndpoint
     */
    useGet: RsmServiceHookFactory.useGet,

    /**
     * Creates conventional hook for using service list function for the supplied resource type.
     * Automatically handles cancellation tokens internally.
     *
     * ### Recommendation
     * Use `useNestedList` when route is nested!
     *
     * @param recordType
     * @param baseEndpoint
     */
    useList<TRecord, TQueryParams>(
        recordType: { new (): TRecord },
        baseEndpoint: string
    ): ListServiceHook<TRecord, TQueryParams> {
        return () => {
            const { cancellablePromise } = useCancellablePromise();

            const serviceList = ServiceFactory.list<TRecord, TQueryParams>(
                recordType,
                baseEndpoint
            );

            function list(
                queryParams?: TQueryParams
            ): Promise<ServiceResponse<TRecord>> {
                return cancellablePromise(serviceList(queryParams)) as Promise<
                    ServiceResponse<TRecord>
                >;
            }

            return { list: useCallback(list, []) };
        };
    },

    /**
     * Creates conventional hook for using batched service nested list function for the supplied resource type.
     * Automatically handles cancellation tokens internally.
     *
     * @param recordType
     * @param baseEndpoint
     * @param property
     * @param size
     */
    useNestedBatchedList<TRecord, TPathParams, TQueryParams>(
        recordType: { new (): TRecord },
        baseEndpoint: string
    ): NestedBatchedListServiceHook<TRecord, TPathParams, TQueryParams> {
        return () => {
            const { cancellablePromise } = useCancellablePromise();

            const serviceList = ServiceFactory.nestedBatchedList<
                TRecord,
                TPathParams,
                TQueryParams
            >(recordType, baseEndpoint);

            function list(
                pathParams: TPathParams,
                queryParams: TQueryParams,
                property: keyof TQueryParams,
                size: number
            ): Promise<ServiceResponse<TRecord>> {
                return cancellablePromise(
                    serviceList(pathParams, queryParams, property, size)
                ) as Promise<ServiceResponse<TRecord>>;
            }

            return { list: useCallback(list, []) };
        };
    },

    /**
     * Creates conventional hook for using service nested create function for the supplied resource type.
     * Automatically handles cancellation tokens internally.
     * @param recordType
     * @param baseEndpoint
     */
    useNestedCreate<TRecord extends RecordType, TPathParams>(
        recordType: { new (): TRecord },
        baseEndpoint: string
    ): NestedCreateServiceHook<TRecord, TPathParams> {
        return () => {
            const { cancellablePromise } = useCancellablePromise();

            const serviceCreate = ServiceFactory.nestedCreate<
                TRecord,
                TPathParams
            >(recordType, baseEndpoint);

            function create(
                record: TRecord,
                pathParams: TPathParams
            ): Promise<ServiceResponse<TRecord>> {
                return cancellablePromise(
                    serviceCreate(record, pathParams)
                ) as Promise<ServiceResponse<TRecord>>;
            }

            return { create: useCallback(create, []) };
        };
    },

    /**
     * Creates conventional hook for using service nested list function for the supplied resource type.
     * Automatically handles cancellation tokens internally.
     * @param recordType
     * @param baseEndpoint
     */
    useNestedList<TRecord, TPathParams, TQueryParams>(
        recordType: { new (): TRecord },
        baseEndpoint: string
    ): NestedListServiceHook<TRecord, TPathParams, TQueryParams> {
        return () => {
            const { cancellablePromise } = useCancellablePromise();

            const serviceList = ServiceFactory.nestedList<
                TRecord,
                TPathParams,
                TQueryParams
            >(recordType, baseEndpoint);

            function list(
                pathParams: TPathParams,
                queryParams?: TQueryParams
            ): Promise<ServiceResponse<TRecord>> {
                return cancellablePromise(
                    serviceList(pathParams, queryParams)
                ) as Promise<ServiceResponse<TRecord>>;
            }

            return { list: useCallback(list, []) };
        };
    },

    /**
     * Creates conventional hook for using service update function for the supplied resource type.
     * Automatically handles cancellation tokens internally.
     * @param recordType
     * @param baseEndpoint
     */
    useUpdate<
        TRecord extends RecordType,
        TPathParams,
        TQueryParams = undefined,
    >(
        recordType: { new (): TRecord },
        resourceEndpoint: string
    ): UpdateServiceHook<TRecord, TPathParams, TQueryParams> {
        return () => {
            const { cancellablePromise } = useCancellablePromise();

            const serviceUpdate = ServiceFactory.update<
                TRecord,
                TPathParams,
                TQueryParams
            >(recordType, resourceEndpoint);

            function update(
                record: TRecord,
                pathParams?: TPathParams,
                queryParams?: TQueryParams
            ): Promise<ServiceResponse<TRecord>> {
                return cancellablePromise(
                    serviceUpdate(record, pathParams, queryParams)
                ) as Promise<ServiceResponse<TRecord>>;
            }

            return { update: useCallback(update, []) };
        };
    },

    /**
     * Creates conventional hook for using service update function for the supplied resource type
     * when updating from parameters in the request body. Automatically handles cancellation tokens
     * internally.
     *
     * @param recordType
     * @param resourceEndpoint
     */
    useUpdateFromPutParams<
        TRecord extends RecordType,
        TPutParams,
        TPathParams,
        TQueryParams = undefined,
    >(
        recordType: { new (): TRecord },
        resourceEndpoint: string
    ): UpdateFromPutParamsServiceHook<
        TRecord,
        TPutParams,
        TPathParams,
        TQueryParams
    > {
        return () => {
            const { cancellablePromise } = useCancellablePromise();

            const serviceUpdate = ServiceFactory.updateFromPutParams<
                TRecord,
                TPutParams,
                TPathParams,
                TQueryParams
            >(recordType, resourceEndpoint);

            function update(
                requestData?: TPutParams,
                pathParams?: TPathParams,
                queryParams?: TQueryParams,
                signal?: AbortSignal
            ): Promise<ServiceResponse<TRecord>> {
                return cancellablePromise(
                    serviceUpdate(requestData, pathParams, queryParams, signal)
                ) as Promise<ServiceResponse<TRecord>>;
            }

            return { update: useCallback(update, []) };
        };
    },

    /**
     * Creates conventional hook for using service update function for an array of the supplied resource type.
     * Automatically handles cancellation tokens internally.
     * @param recordType
     * @param resourceEndpoint
     */
    useBulkUpdate<TRecord extends RecordType, TPathParams>(
        recordType: { new (): TRecord },
        resourceEndpoint: string
    ): BulkUpdateServiceHook<TRecord, TPathParams> {
        return () => {
            const { cancellablePromise } = useCancellablePromise();

            const serviceUpdate = ServiceFactory.bulkUpdate(
                recordType,
                resourceEndpoint
            );

            function update(
                records: Array<TRecord>,
                pathParams: TPathParams
            ): Promise<ServiceResponse<TRecord>> {
                return cancellablePromise(
                    serviceUpdate(records, pathParams)
                ) as Promise<ServiceResponse<TRecord>>;
            }

            return { update: useCallback(update, []) };
        };
    },

    /**
     * Creates conventional hook for using service stream function for the supplied resource type.
     *
     * @param RecordType
     * @param resourceEndpoint
     */
    useStream<TRecord extends RecordType, TPathParams>(
        RecordType: new (props: Partial<TRecord>) => TRecord,
        resourceEndpoint: string
    ): StreamServiceHook<TRecord, TPathParams> {
        return () => {
            const signal = useAbortSignal();

            const serviceStream = ServiceFactory.stream<TRecord, TPathParams>(
                RecordType,
                resourceEndpoint
            );

            return {
                stream: useCallback(
                    function stream(
                        record: TRecord,
                        pathParams?: TPathParams
                    ): ReadableStream<ServiceResponse<TRecord>> {
                        return serviceStream(record, pathParams, signal);
                    },
                    [signal]
                ),
            };
        };
    },
};

// #endregion Functions

// ---------------------------------------------------------------------------------------------
// #region Exports
// ---------------------------------------------------------------------------------------------

export default ServiceHookFactory;

// #endregion Exports
